import { useState, useMemo } from "react";
import { Loader2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { RankTrackerKeyword } from "@/types";
import { toast } from "@/hooks/use-toast";
import { useAppData } from "@/contexts/AppDataContext";
import { rankTrackerApi } from "@/resources/rank-tracker";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { compareNullableNumber } from "@/utils/keywordMetricDisplay";
import { SortIcon, type SortState, type SortField } from "./SortIcon";
import { KeywordDataRow } from "./KeywordDataRow";
import { COLUMN_TOOLTIPS } from "./constants";

interface KeywordRowsTableProps {
  heroProductId: number;
  asin: string;
  productName: string;
  publicKeywords?: RankTrackerKeyword[];
  dateFrom?: string;
  dateTo?: string;
}

export const KeywordRowsTable = ({ heroProductId, asin, productName, publicKeywords, dateFrom, dateTo }: KeywordRowsTableProps) => {
  const viewerReadOnly = useViewerReadOnly();
  const isPublic = publicKeywords !== undefined;
  const { selectedBrandId } = useAppData();
  const { data: fetchedKeywords = [], isLoading, isFetching } = rankTrackerApi.useGetRankTrackerKeywords(
    isPublic ? null : heroProductId,
    isPublic ? null : selectedBrandId,
    dateFrom,
    dateTo,
  );
  const keywords = isPublic ? publicKeywords : fetchedKeywords;
  const { mutate: deleteKeyword } = rankTrackerApi.useDeleteRankTrackerKeyword();

  const [sort, setSort] = useState<SortState>({ field: "currentRank", direction: "asc" });

  const handleStopTracking = (keywordId: number, keywordText: string) => {
    if (!selectedBrandId) return;
    deleteKeyword(
      { id: keywordId, brandId: selectedBrandId },
      {
        onSuccess: () => {
          toast({
            title: "Keyword Removed",
            description: `"${keywordText}" is no longer being tracked.`,
          });
        },
      }
    );
  };

  const toggleSort = (field: SortField) => {
    setSort((prev) => {
      if (prev.field !== field) return { field, direction: "asc" };
      if (prev.direction === "asc") return { field, direction: "desc" };
      return { field: null, direction: null };
    });
  };

  const sortedKeywords = useMemo(() => {
    const result = [...(keywords ?? [])];
    if (sort.field && sort.direction) {
      const dir = sort.direction === "asc" ? 1 : -1;
      const direction = sort.direction;
      const rankChangeFor = (k: RankTrackerKeyword): number | null =>
        k.previousRank != null && k.currentRank != null ? k.previousRank - k.currentRank : null;
      result.sort((a, b) => {
        switch (sort.field) {
          case "keyword":
            return dir * a.keyword.localeCompare(b.keyword);
          case "currentRank":
            return compareNullableNumber(a.currentRank, b.currentRank, direction);
          case "rankChange":
            return compareNullableNumber(rankChangeFor(a), rankChangeFor(b), direction);
          case "searchVolume":
            return compareNullableNumber(a.searchVolume, b.searchVolume, direction);
          case "bestRank":
            return compareNullableNumber(a.bestRank, b.bestRank, direction);
          case "dateAdded":
            return dir * (new Date(a.dateAdded).getTime() - new Date(b.dateAdded).getTime());
          default:
            return 0;
        }
      });
    }
    return result;
  }, [keywords, sort]);

  if (!isPublic && isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading keywords...</span>
      </div>
    );
  }

  const showActionsColumn = !isPublic && !viewerReadOnly;
  const colSpan = isPublic ? 5 : viewerReadOnly ? 5 : 6;

  return (
    <TooltipProvider delayDuration={0}>
      <div className="space-y-3">
        <div className="relative">
          {isFetching && !isLoading && !isPublic && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50 backdrop-blur-[1px]">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          <div>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent bg-muted/70">
                  <TableHead className="w-[280px] h-9 py-2 px-3 text-xs font-semibold text-foreground cursor-pointer select-none" onClick={() => toggleSort("keyword")}>
                    <span className="inline-flex items-center gap-1">
                      <Tooltip>
                        <TooltipTrigger className="cursor-help border-b border-dotted border-muted-foreground" onClick={(e) => e.preventDefault()}>
                          Keyword Phrase
                        </TooltipTrigger>
                        <TooltipContent side="bottom"><p>{COLUMN_TOOLTIPS.keyword}</p></TooltipContent>
                      </Tooltip>
                      <SortIcon field="keyword" sort={sort} />
                    </span>
                  </TableHead>
                  <TableHead className="w-[100px] h-9 py-2 px-3 text-xs font-semibold text-foreground cursor-pointer select-none" onClick={() => toggleSort("searchVolume")}>
                    <span className="inline-flex items-center gap-1">
                      <Tooltip>
                        <TooltipTrigger className="cursor-help border-b border-dotted border-muted-foreground" onClick={(e) => e.preventDefault()}>
                          Search Vol.
                        </TooltipTrigger>
                        <TooltipContent side="bottom"><p>{COLUMN_TOOLTIPS.searchVolume}</p></TooltipContent>
                      </Tooltip>
                      <SortIcon field="searchVolume" sort={sort} />
                    </span>
                  </TableHead>
                  <TableHead className="w-[100px] h-9 py-2 px-3 text-xs font-semibold text-foreground cursor-pointer select-none" onClick={() => toggleSort("currentRank")}>
                    <span className="inline-flex items-center gap-1">
                      <Tooltip>
                        <TooltipTrigger className="cursor-help border-b border-dotted border-muted-foreground" onClick={(e) => e.preventDefault()}>
                          Organic Rank
                        </TooltipTrigger>
                        <TooltipContent side="bottom"><p>{COLUMN_TOOLTIPS.organicRank}</p></TooltipContent>
                      </Tooltip>
                      <SortIcon field="currentRank" sort={sort} />
                    </span>
                  </TableHead>
                  <TableHead className="w-[80px] h-9 py-2 px-3 text-xs font-semibold text-foreground cursor-pointer select-none" onClick={() => toggleSort("rankChange")}>
                    <span className="inline-flex items-center gap-1">
                      <Tooltip>
                        <TooltipTrigger className="cursor-help border-b border-dotted border-muted-foreground" onClick={(e) => e.preventDefault()}>
                          Change
                        </TooltipTrigger>
                        <TooltipContent side="bottom"><p>{COLUMN_TOOLTIPS.change}</p></TooltipContent>
                      </Tooltip>
                      <SortIcon field="rankChange" sort={sort} />
                    </span>
                  </TableHead>
                  <TableHead className="w-[100px] h-9 py-2 px-3 text-xs font-semibold text-foreground cursor-pointer select-none" onClick={() => toggleSort("dateAdded")}>
                    <span className="inline-flex items-center gap-1">
                      <Tooltip>
                        <TooltipTrigger className="cursor-help border-b border-dotted border-muted-foreground" onClick={(e) => e.preventDefault()}>
                          Date Added
                        </TooltipTrigger>
                        <TooltipContent side="bottom"><p>{COLUMN_TOOLTIPS.dateAdded}</p></TooltipContent>
                      </Tooltip>
                      <SortIcon field="dateAdded" sort={sort} />
                    </span>
                  </TableHead>
                  {showActionsColumn ? <TableHead className="w-[50px] h-9 py-2 px-3 text-xs" /> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedKeywords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} className="h-16 text-center text-xs text-muted-foreground">
                      No keywords found.
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedKeywords.map((keyword) => (
                    <KeywordDataRow
                      key={keyword.id}
                      keyword={keyword}
                      isPublic={isPublic}
                      showActionsColumn={showActionsColumn}
                      onStopTracking={handleStopTracking}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};
